import { useLayoutEffect, useRef } from 'react';
import { Filter, X } from 'lucide-react';
import { FLOOR_PLAN_KIND_LABEL } from '@obraplan/core/domain/takeoff/floor-plan';
import type { UnitTypeId } from '@obraplan/core/domain/shared/ids';
import { TabList, type TabItem } from '@/components/ui/tabs';
import { cn } from '@/lib/cn';
import type { FiltroDoQuantitativo } from './escopo-da-tipologia';
import { nomeDaTipologia, rotuloDoTipo } from './area-unica';
import type { TypologyDraft } from './typology-draft';

const SELECT_CLASS =
  'h-11 min-w-0 rounded-md border border-(--color-border-subtle) bg-(--color-surface) px-2 text-sm md:h-9';

export interface OpcaoDoFiltro {
  readonly key: string;
  readonly name: string;
}

/**
 * A faixa que fica presa no topo do quantitativo.
 *
 * Três perguntas que valem para as duas abas, e que a tela respondia em lugares diferentes
 * — ou não respondia: **em qual tipologia estou** (e quantas vezes ela acontece), **em qual
 * aba**, e **o que estou vendo** (o filtro). Rolando uma lista de oitenta serviços, a aba e
 * a tipologia sumiam de vista, e trocar exigia voltar ao topo.
 *
 * O filtro são dois menus, e não uma faixa de etiquetas: com trinta e oito disciplinas as
 * etiquetas ocupavam três linhas e empurravam o conteúdo para baixo. Ligado, ele aparece
 * como selo com "limpar", para ninguém confundir a lista filtrada com a compra inteira.
 */
export function FaixaDoQuantitativo<Aba extends string>({
  tipologias,
  ativa,
  vezes,
  onTipologia,
  abas,
  aba,
  onAba,
  filtro,
  disciplinas,
  servicos,
  onFiltro,
  acoes,
}: {
  tipologias: readonly TypologyDraft[];
  ativa: TypologyDraft;
  /** Quantas vezes a tipologia acontece na obra — o multiplicador do total. */
  vezes: number;
  onTipologia: (unitTypeId: UnitTypeId) => void;
  abas: readonly TabItem<Aba>[];
  aba: Aba;
  onAba: (aba: Aba) => void;
  filtro: FiltroDoQuantitativo;
  /** As disciplinas que o filtro oferece — as da tipologia aberta. */
  disciplinas: readonly OpcaoDoFiltro[];
  /** Os serviços da disciplina escolhida no filtro. */
  servicos: readonly OpcaoDoFiltro[];
  onFiltro: (filtro: FiltroDoQuantitativo) => void;
  /** O que mais mora no canto da faixa — o "Recomeçar". */
  acoes?: React.ReactNode;
}) {
  const disciplina = filtro.disciplina;
  const nomeDaDisciplina =
    disciplinas.find((opcao) => opcao.key === disciplina)?.name ?? disciplina;
  const nomeDoServico = servicos.find((opcao) => opcao.key === filtro.servico)?.name;
  const filtrado = disciplina !== undefined;
  // O endereço pode trazer uma disciplina que esta tipologia não tem — veio de outra, ou
  // de um link antigo. Ela fica no menu, marcada, para o selo não mentir sobre o que filtra.
  const disciplinaDeFora =
    disciplina !== undefined && !disciplinas.some((opcao) => opcao.key === disciplina);

  // A altura da faixa vai para a página (`--altura-da-faixa`): o que também gruda no topo — o
  // card "O que sai de…" — gruda logo abaixo dela, e não por baixo. A faixa quebra em duas
  // linhas conforme a largura e o filtro, e por isso a altura é medida, e não um número fixo.
  const faixa = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const elemento = faixa.current;
    const pagina = elemento?.parentElement;
    if (elemento === null || pagina === null || pagina === undefined) return;
    const publicar = () => {
      pagina.style.setProperty('--altura-da-faixa', `${String(elemento.offsetHeight)}px`);
    };
    publicar();
    if (typeof ResizeObserver === 'undefined') return;
    const observador = new ResizeObserver(publicar);
    observador.observe(elemento);
    return () => {
      observador.disconnect();
      pagina.style.removeProperty('--altura-da-faixa');
    };
  }, []);

  return (
    <div
      ref={faixa}
      className={cn(
        // Presa no topo no computador, onde a lista é longa e a faixa cabe. No celular o
        // cabeçalho do sistema já é fixo, e duas faixas fixas comeriam metade da tela.
        '-mx-4 flex flex-col gap-2 border-b border-(--color-border-subtle) bg-(--color-surface) px-4 pb-2 pt-1 md:sticky md:top-0 md:z-20 md:-mx-6 md:px-6 md:pt-3',
      )}
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <h1 className="text-xl font-semibold">Quantitativo</h1>
        <label className="flex min-w-0 items-center gap-2">
          <span className="sr-only">Tipologia aberta</span>
          <select
            className={cn(SELECT_CLASS, 'max-w-72 font-medium text-(--color-brand)')}
            value={ativa.unitTypeId}
            onChange={(event) => onTipologia(event.target.value as UnitTypeId)}
          >
            {tipologias.map((tipologia) => (
              <option key={tipologia.unitTypeId} value={tipologia.unitTypeId}>
                {nomeDaTipologia(tipologia)}
              </option>
            ))}
          </select>
        </label>
        <span className="tabular text-sm text-(--color-content-muted)">
          <strong className="font-semibold text-(--color-content)">
            × {vezes} {vezes === 1 ? 'vez' : 'vezes'}
          </strong>{' '}
          na obra · {FLOOR_PLAN_KIND_LABEL[ativa.floorKind]} · {rotuloDoTipo(ativa)}
        </span>
        {acoes !== undefined && <div className="ml-auto">{acoes}</div>}
      </div>

      <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-2">
        <TabList
          base="quantitativo"
          items={abas}
          atual={aba}
          onEscolher={onAba}
          label="Partes do quantitativo"
          variant="underline"
        />

        <div className="flex flex-wrap items-center gap-2 pb-1">
          <Filter className="size-4 shrink-0 text-(--color-content-muted)" aria-hidden="true" />
          <select
            aria-label="Filtrar por disciplina"
            className={cn(SELECT_CLASS, 'max-w-56')}
            value={disciplina ?? ''}
            onChange={(event) =>
              onFiltro(event.target.value === '' ? {} : { disciplina: event.target.value })
            }
          >
            <option value="">Todas as disciplinas</option>
            {disciplinaDeFora && (
              <option value={disciplina}>{disciplina} (fora desta tipologia)</option>
            )}
            {disciplinas.map((opcao) => (
              <option key={opcao.key} value={opcao.key}>
                {opcao.name}
              </option>
            ))}
          </select>
          <select
            aria-label="Filtrar por serviço"
            className={cn(SELECT_CLASS, 'max-w-56')}
            value={filtro.servico ?? ''}
            disabled={disciplina === undefined}
            title={disciplina === undefined ? 'Escolha a disciplina primeiro' : undefined}
            onChange={(event) =>
              onFiltro({
                ...(disciplina === undefined ? {} : { disciplina }),
                ...(event.target.value === '' ? {} : { servico: event.target.value }),
              })
            }
          >
            <option value="">Todos os serviços</option>
            {servicos.map((opcao) => (
              <option key={opcao.key} value={opcao.key}>
                {opcao.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {filtrado && (
        <p
          role="status"
          className="flex w-fit flex-wrap items-center gap-2 rounded-md border border-(--color-state-info) bg-(--color-surface-muted) px-2 py-1 text-xs"
        >
          <span>
            <strong className="font-semibold">Filtrado:</strong> {nomeDaDisciplina}
            {nomeDoServico !== undefined && ` › ${nomeDoServico}`} — listas, aptos, consolidação e
            orçamento mostram só isto.
          </span>
          <button
            type="button"
            className="inline-flex items-center gap-1 font-medium text-(--color-brand) underline-offset-4 hover:underline"
            onClick={() => onFiltro({})}
          >
            <X className="size-3.5" aria-hidden="true" />
            limpar
          </button>
        </p>
      )}
    </div>
  );
}
